import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import Header from "@/components/landing/Header";
import Hero from "@/components/landing/Hero";
import SocialProof from "@/components/landing/SocialProof";
import ProblemSolution from "@/components/landing/ProblemSolution";
import Services from "@/components/landing/Services";
import CaseStudies from "@/components/landing/CaseStudies";
import Testimonials from "@/components/landing/Testimonials";
import HowItWorks from "@/components/landing/HowItWorks";
import Pricing from "@/components/landing/Pricing";
import FAQ from "@/components/landing/FAQ";
import LeadMagnet from "@/components/landing/LeadMagnet";
import About from "@/components/landing/About";
import FinalCTA from "@/components/landing/FinalCTA";
import Footer from "@/components/landing/Footer";
import StickyMobileCTA from "@/components/landing/StickyMobileCTA";
import { useLang } from "@/context/LanguageContext";

const META = {
  en: {
    title: "Moroccan AI Factory — AI Agency in Morocco | Agents & Automation",
    description:
      "AI agency in Morocco. We build AI agents, chatbots, business automation & cloud DevOps for SMEs in Casablanca, Rabat, Marrakech & across Africa.",
  },
  fr: {
    title: "Moroccan AI Factory — Agence IA au Maroc | Agents & Automatisation",
    description:
      "Agence IA au Maroc. Nous concevons des agents IA, chatbots, automatisation métier et CloudOps pour les PME à Casablanca, Rabat, Marrakech et en Afrique.",
  },
  ar: {
    title: "Moroccan AI Factory — وكالة ذكاء اصطناعي في المغرب",
    description:
      "وكالة ذكاء اصطناعي في المغرب. نبني وكلاء AI وشات‑بوتات وأتمتة أعمال وعمليات سحابية للشركات في الدار البيضاء والرباط ومراكش وعبر إفريقيا.",
  },
};

const LandingPage = () => {
  const { lang } = useLang();
  const location = useLocation();
  const meta = META[lang] || META.en;

  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-visible");
            obs.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  // Smooth-scroll to in-page anchor when navigating from another route (e.g. /#services)
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      const tryScroll = (attempt = 0) => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        } else if (attempt < 10) {
          setTimeout(() => tryScroll(attempt + 1), 100);
        }
      };
      tryScroll();
    }
  }, [location.hash]);

  const websiteJsonLd = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Moroccan AI Factory",
    url: "https://maif.ma",
    inLanguage: ["en", "fr", "ar"],
    potentialAction: {
      "@type": "SearchAction",
      target: "https://maif.ma/blog?q={search_term_string}",
      "query-input": "required name=search_term_string",
    },
  };

  return (
    <div data-testid="landing-page" className="bg-maif text-maif min-h-screen">
      <Helmet>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href="https://maif.ma/" />
        <link rel="alternate" hrefLang="en" href="https://maif.ma/" />
        <link rel="alternate" hrefLang="fr" href="https://maif.ma/" />
        <link rel="alternate" hrefLang="ar" href="https://maif.ma/" />
        <link rel="alternate" hrefLang="x-default" href="https://maif.ma/" />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content="https://maif.ma/" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content={lang === "fr" ? "fr_FR" : lang === "ar" ? "ar_MA" : "en_US"} />
        <meta name="twitter:title" content={meta.title} />
        <meta name="twitter:description" content={meta.description} />
        <script type="application/ld+json">{JSON.stringify(websiteJsonLd)}</script>
      </Helmet>
      <Header />
      <main>
        <Hero />
        <SocialProof />
        <ProblemSolution />
        <Services />
        <CaseStudies />
        <Testimonials />
        <HowItWorks />
        <Pricing />
        <FAQ />
        <LeadMagnet />
        <About />
        <FinalCTA />
      </main>
      <Footer />
      <StickyMobileCTA />
    </div>
  );
};

export default LandingPage;
